import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {Context} from '../contexts/WordList';
import {FixedSizeList as VirtualList} from 'react-window';
// Infinite loading removed — we load all words on startup
import {AddWordForm} from '../components/AddWordForm';
import {MediaPlayer} from '../components/MediaPlayer';
import {WordDetailsDrawer} from '../components/WordDetailsDrawer';
import {useLoadWords} from '../effects/useLoadWords';
import {useAddWord} from '../effects/useAddWord';
import {useAddCategory} from '../effects/useAddCategory';
import {useDeleteCategory} from '../effects/useDeleteCategory';
import {useEditWord} from '../effects/useEditWord';
import {useDeleteWord} from '../effects/useDeleteWord';
import {getTagIcon} from '../helpers/tagIcons';
import {fetchStudyProgress} from '../helpers/localApi';
import {
  generateSentences,
  suggestTranslation,
  suggestTag,
} from '../helpers/externalApi';
//import { useTestCase as useTestAddWord } from '../effects/useTestCase';
//import {useCurrentDb} from '../helpers/dbhelper';

// Maps a study-progress record to a status bucket class used for the row
// colour indicator. Buckets: new → learning → familiar → review → mastered.
const MASTERY_MASTERED = 4;
function masteryStatusClass(progress) {
  if (!progress || !progress.masteryLevel) return 'word-status-new';
  const level = progress.masteryLevel;
  if (level >= MASTERY_MASTERED) return 'word-status-mastered';
  if (level >= 3) return 'word-status-review';
  if (level >= 2) return 'word-status-familiar';
  return 'word-status-learning';
}


export const WordList = () => {
  const [state, dispatch] = useContext(Context);
  const {items, language} = state;
  const [displayAddForm, setDisplayAddForm] = useState(false);
  const [selectedWordKey, setSelectedWordKey] = useState('');
  //const words = useCurrentDb();

  useLoadWords();
  const {addWord} = useAddWord();
  const {addCategory} = useAddCategory();
  const {deleteCategory} = useDeleteCategory();
  const {editWord} = useEditWord();
  const {deleteWord} = useDeleteWord();
  //const { addWordTest} = useTestAddWord();

  // const classes = useStyles();
  const {height, width} = useWindowDimensions();

 // useEffect(()=> {addWordTest()}, []);

  function getWindowDimensions() {
    const {innerWidth: width, innerHeight: height} = window;
    return {
      width,
      height,
    };
  }

  function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
      function handleResize() {
        setWindowDimensions(getWindowDimensions());
      }

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowDimensions;
  }

  useEffect(() => {
    // console.log('************ wordlist ******************');
    dispatch({
      type: 'HAS_MORE_ITEMS',
      payload: true,
    });
  }, [dispatch]);

  // Study progress for status indicators (mastery colour on rows, drawer stats).
  const [progressMap, setProgressMap] = useState({});
  useEffect(() => {
    if (!language) return undefined;
    let cancelled = false;
    fetchStudyProgress({language})
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data || []).forEach((p) => {
          map[p.wordId] = p;
        });
        setProgressMap(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [language, items.length]);

  const handleConfirmationAddWord = (wordEn, wordFa, transliteration) => {
    setDisplayAddForm(false);

    addWord(wordEn, wordFa, transliteration);
  };

  const handleBulkAddWords = async (payload) => {
    const entries = Array.isArray(payload) ? payload : payload && payload.entries;
    const options = Array.isArray(payload) ? {} : ((payload && payload.options) || {});
    const onProgress = payload && typeof payload.onProgress === 'function' ?
      payload.onProgress :
      null;

    if (!Array.isArray(entries) || entries.length === 0) {
      return;
    }

    const total = entries.length;
    const fillTag = Boolean(options.fillTag);
    const fillSentences = Boolean(options.fillSentences);
    let completed = 0;
    let failed = 0;

    onProgress && onProgress({
      stage: 'starting',
      current: 0,
      total,
      completed,
      failed,
      message: `Adding ${total} words...`,
    });

    for (let index = 0; index < entries.length; index++) {
      const entry = entries[index];
      const current = index + 1;
      let wordEn = entry.wordEn || '';
      let wordFa = entry.wordFa || '';
      let transliteration = entry.transliteration || '';

      onProgress && onProgress({
        stage: 'processing',
        current,
        total,
        completed,
        failed,
        wordEn,
        message: `Adding ${wordEn} (${current}/${total})`,
      });

      try {
        if (!wordFa && wordEn) {
          onProgress && onProgress({
            stage: 'translating',
            current,
            total,
            completed,
            failed,
            wordEn,
            message: `Generating translation for ${wordEn} (${current}/${total})`,
          });

          const translated = await suggestTranslation({
            word: wordEn,
            fromLang: 'English',
            toLang: language === 'hebrew' ? 'Hebrew' : 'Farsi',
          });
          wordFa = translated.translation || wordFa;
          if (!transliteration && translated.transliteration) {
            transliteration = translated.transliteration;
          }
        }

        let sentencesJson = '';

        if (fillSentences) {
          onProgress && onProgress({
            stage: 'sentences',
            current,
            total,
            completed,
            failed,
            wordEn,
            message: `Generating sample sentences for ${wordEn} (${current}/${total})`,
          });

          const generatedSentences = await generateSentences({
            wordEn,
            wordFa,
            language,
          });
          if (Array.isArray(generatedSentences) && generatedSentences.length > 0) {
            sentencesJson = JSON.stringify(generatedSentences);
          }
        }

        onProgress && onProgress({
          stage: 'saving',
          current,
          total,
          completed,
          failed,
          wordEn,
          message: `Saving ${wordEn} (${current}/${total})`,
        });

        const createdWord = await addWord(
            wordEn,
            wordFa,
            transliteration,
            {
              sentences: sentencesJson,
              sourceUrl: entry.sourceUrl || '',
              sourceTitle: entry.sourceTitle || '',
            },
        );

        if (fillTag && createdWord && createdWord.key) {
          onProgress && onProgress({
            stage: 'tagging',
            current,
            total,
            completed,
            failed,
            wordEn,
            message: `Generating tag for ${wordEn} (${current}/${total})`,
          });

          const suggestedTag = await suggestTag({
            wordEn,
            wordFa,
          });
          await addCategory(createdWord.key, suggestedTag);
        }

        completed += 1;
      } catch (error) {
        failed += 1;
        // eslint-disable-next-line no-console
        console.warn('Bulk add entry failed:', wordEn, error);
      }

      onProgress && onProgress({
        stage: 'progress',
        current,
        total,
        completed,
        failed,
        wordEn,
        message: `Progress: ${completed}/${total} added${failed ? `, ${failed} failed` : ''}`,
      });
    }

    onProgress && onProgress({
      stage: 'done',
      current: total,
      total,
      completed,
      failed,
      message: `Finished: ${completed}/${total} added${failed ? `, ${failed} failed` : ''}`,
    });
  };

  const handleCloseAddWord = () => {
    setDisplayAddForm(false);
  };

  const handleDisplayAddForm = () => {
    setDisplayAddForm(true);
  };


  const tagsFilterSet = useMemo(
      () => new Set(state.tagsFilter),
      [state.tagsFilter],
  );

  const filteredItems = useMemo(
      () => items.filter((item) => {
        const tags = Array.isArray(item.tags) ? item.tags : [];
        const hasMatchingTag = tags.some((tag) => tagsFilterSet.has(tag));

        if (hasMatchingTag) {
          return true;
        }

        return tagsFilterSet.has('Untagged') &&
          (typeof item.tags === 'undefined' ||
            item.tags === null ||
            item.tags.length === 0);
      }),
      [items, tagsFilterSet],
  );
  const selectedWord = useMemo(
      () => items.find((item) => item.key === selectedWordKey),
      [items, selectedWordKey],
  );

    const selectedWordIndex = useMemo(
      () => filteredItems.findIndex((item) => item.key === selectedWordKey),
      [filteredItems, selectedWordKey],
    );

  // Rows hold title + translation + optional transliteration + up to one
  // wrapped tag line, so the fixed virtual-row height must cover all four.
  const rowHeight = width <= 520 ? 138 : width <= 768 ? 122 : 108;
  // Subtract the surrounding chrome (topbar incl. search row, list header,
  // bottom tab bar on mobile) so the virtual list fills the remaining
  // viewport without forcing an outer page scroll on phones.
  const chromeHeight = width <= 640 ? 330 : 200;
  const listHeight = Math.max(320, height - chromeHeight);

  const handleCloseDrawer = () => {
    setSelectedWordKey('');
  };

  const handleShowNextWord = () => {
    if (selectedWordIndex < 0 || selectedWordIndex >= filteredItems.length - 1) {
      return;
    }

    setSelectedWordKey(filteredItems[selectedWordIndex + 1].key);
  };

  const handleShowPreviousWord = () => {
    if (selectedWordIndex <= 0) {
      return;
    }

    setSelectedWordKey(filteredItems[selectedWordIndex - 1].key);
  };

  const renderRow = useCallback(({index, style}) => {
    const item = filteredItems[index];
    if (!item) {
      return null;
    }

    const primary = state.orderBy === 'wordEn' ? item.wordEn : item.wordFa;
    const secondary = !state.hideTranslation ?
      (state.orderBy === 'wordEn' ? item.wordFa : item.wordEn) :
      null;
    const tags = Array.isArray(item.tags) ? item.tags : [];
    const visibleTags = tags.slice(0, 3);
    const hiddenTagCount = tags.length - visibleTags.length;
    const langFont = state.language === 'farsi' ? 'font-farsi' : 'font-hebrew';
    const statusClass = masteryStatusClass(progressMap[item.key]);

    return (
      <div className="word-row" style={style} key={item.key}>
        <button
          type="button"
          className={`word-row-btn${statusClass ? ` ${statusClass}` : ''}`}
          onClick={() => setSelectedWordKey(item.key)}
        >
          <div className="word-row-content">
            <div className={`word-row-primary${state.orderBy !== 'wordEn' ? ` ${langFont}` : ''}`}>{primary}</div>
            {secondary && (
              <div className={`word-row-secondary${state.orderBy === 'wordEn' ? ` ${langFont}` : ''}`}>{secondary}</div>
            )}
            {item.transliteration && !state.hideTranslation && (
              <div className="word-row-transliteration">{item.transliteration}</div>
            )}
            {tags.length > 0 && (
              <div className="word-row-tags">
                {visibleTags.map((tag) => (
                  <span key={tag} className="word-row-tag" data-tag={tag.toLowerCase()}>
                    {getTagIcon(tag)} {tag}
                  </span>
                ))}
                {hiddenTagCount > 0 && (
                  <span className="word-row-tag" title={tags.slice(3).join(', ')}>
                    +{hiddenTagCount}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="word-row-actions">
            {item.mediaUrl &&
              (item.mediaUrl.includes('.m4a') || item.mediaUrl.includes('.mp3')) && (
                <MediaPlayer mediaUrl={item.mediaUrl} />
              )}
            {item.mediaUrl &&
              !item.mediaUrl.includes('.m4a') &&
              !item.mediaUrl.includes('.mp3') && (
                <span className="word-row-media-icon" aria-hidden="true">🖼</span>
              )}
            <span className="word-row-chevron" aria-hidden="true">›</span>
          </div>
        </button>
      </div>
    );
  }, [filteredItems, state.hideTranslation, state.orderBy, state.language, progressMap]);

  const selectedProgress = selectedWord ? progressMap[selectedWord.key] : null;


  // const handleUpdateWord = (item, wordEn, wordFa) => {
    
  //   console.log('update word:' + item.wordEn + ' ' + item.wordFa);
  //   const itemRef = words.doc(item.key);
  //   itemRef
  //     .update({ wordFa: wordFa, wordEn: wordEn.charAt(0).toUpperCase() + wordEn.slice(1)})
  //     .catch((error) => {
  //       console.error('Error updating document: ', error);
  //     });
  // };

  return (
    <>
      <div className="page-container">
        <div className="word-list-header">
          <span className="word-list-count">
            {filteredItems.length} {filteredItems.length === 1 ? 'word' : 'words'}
          </span>
          <span className="word-list-gesture-hint">Tap to edit · Swipe in drawer to move</span>
        </div>
        {filteredItems.length === 0 ? (
          <div className="word-list-empty">
            <div className="word-list-empty-icon"><span role="img" aria-label="Books">📚</span></div>
            <h3>No words yet</h3>
            <p>Tap the <strong>+</strong> button to add your first vocabulary word.</p>
          </div>
        ) : (
          <div className="word-list-shell">
            <VirtualList
              height={listHeight}
              itemCount={filteredItems.length}
              itemSize={rowHeight}
              width="100%"
            >
              {renderRow}
            </VirtualList>
          </div>
        )}
      </div>
      
      <button
        type="button"
        className="fab-add"
        aria-label="add word"
        onClick={handleDisplayAddForm}
      >
        +
      </button>
      <AddWordForm
        displayConfirmPopup={displayAddForm}
        onConfirmation={handleConfirmationAddWord}
        onBulkAdd={handleBulkAddWords}
        onClose={handleCloseAddWord}
      />

      <WordDetailsDrawer
        open={Boolean(selectedWord)}
        onClose={handleCloseDrawer}
        selectedWord={selectedWord}
        progress={selectedProgress}
        hasNextWord={selectedWordIndex >= 0 && selectedWordIndex < filteredItems.length - 1}
        hasPreviousWord={selectedWordIndex > 0}
        onShowNextWord={handleShowNextWord}
        onShowPreviousWord={handleShowPreviousWord}
        language={language}
        editWord={editWord}
        deleteWord={deleteWord}
        addCategory={addCategory}
        deleteCategory={deleteCategory}
      />

    </>
  );
};
